import { useState, useMemo, useCallback } from "react";
import { DEFAULT_VISIBLE_STATES, ALL_STATES } from "@/lib/agendaStates";

/**
 * useAgendaFilters — gestisce la logica multi-select v6 esattamente come
 * specificato dall'utente:
 *
 *   - "Tutti" attivo di default → mostra Bozze + Inviati + Confermati + Prove
 *     (Rifiutati esclusi finché non vengono attivati esplicitamente).
 *   - Tap su una categoria diversa da "Tutti" mentre "Tutti" è attivo →
 *     "Tutti" si disattiva e si attiva SOLO quella categoria.
 *   - Tap su una seconda categoria → si aggiunge (OR logico).
 *   - Tap di nuovo su una categoria attiva → si disattiva.
 *   - Se tutte le categorie tornano disattivate → "Tutti" si riattiva.
 *   - Tap su "Tutti" mentre altre sono attive → reset, "Tutti" diventa unico.
 *   - Stessa identica logica per i filtri Operatori (separato).
 *
 * Il filtro "Rifiutati" è un opt-in esplicito: anche con "Tutti" attivo,
 * gli eventi rifiutati restano nascosti finché l'utente non clicca il chip
 * dedicato.
 */
export function useAgendaFilters({ teamIds = [] } = {}) {
  const [states, setStates] = useState(() => new Set()); // empty = "Tutti"
  const [teams, setTeams] = useState(() => new Set()); // empty = "Tutti team"
  const [onlyMine, setOnlyMine] = useState(false);
  const [showRejected, setShowRejected] = useState(false);

  const statesAllActive = states.size === 0;
  const teamsAllActive = teams.size === 0;

  const toggleState = useCallback((s) => {
    setStates((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
  }, []);

  const toggleTeam = useCallback((id) => {
    setTeams((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const setAllStates = useCallback(() => setStates(new Set()), []);
  const setAllTeams = useCallback(() => setTeams(new Set()), []);

  /**
   * isVisible — applica i due filtri (stato + team) in AND.
   * `event.state` ∈ {bozza, inviato, confermato, rifiutato, prova}
   * `event.assignedTeamIds` ∈ array di id team o []
   */
  const isVisible = useCallback(
    (event) => {
      // Rifiutati: gated by explicit opt-in
      if (event.state === "rifiutato" && !showRejected) return false;

      // Filtro stato (multi-select)
      if (!statesAllActive) {
        if (!states.has(event.state)) return false;
      } else {
        // "Tutti" attivo → mostra solo gli stati visibili di default + (rifiutato se opt-in)
        if (!DEFAULT_VISIBLE_STATES.includes(event.state) && event.state !== "rifiutato") return false;
        if (event.state === "rifiutato" && !showRejected) return false;
      }

      // Filtro team (multi-select)
      if (!teamsAllActive) {
        const eventTeams = event.assignedTeamIds || [];
        if (!eventTeams.some((id) => teams.has(id))) return false;
      }

      // Solo miei (richiede ownerId match — il backend già filtra per owner_id,
      // ma "solo miei" lato UI può ulteriormente filtrare se assigned_to_me)
      if (onlyMine) {
        if (event.kind === "personal_block") {
          // i personal block sono sempre miei (filtrati dal backend)
        } else {
          // Per i wedding/trial: "miei" significa che sono assegnato come team
          if (!event.assignedToMe) return false;
        }
      }

      return true;
    },
    [states, teams, statesAllActive, teamsAllActive, onlyMine, showRejected],
  );

  return {
    states,
    teams,
    statesAllActive,
    teamsAllActive,
    onlyMine,
    showRejected,
    toggleState,
    toggleTeam,
    setAllStates,
    setAllTeams,
    setOnlyMine,
    setShowRejected,
    isVisible,
    ALL_STATES,
  };
}
